// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Injectable } from '@angular/core';
import { Observable, firstValueFrom, from, map } from 'rxjs';
import { SettingsService } from './settings.service';
import { OemSettings } from '@interfaces/settings.interfaces';
import { UserPermissionIds } from '@interfaces/user.interfaces';
import { UserService } from './user.service';

@Injectable({
    providedIn: 'root',
})
export class ContentService {
    constructor(
        private readonly settingsService: SettingsService,
        private readonly userService: UserService
    ) {}

    private getSettings(oemCode?: string): Observable<OemSettings> {
        return from(this.settingsService.getOemSettings(oemCode));
    }

    public permissionsMissingError(oemCode?: string): Observable<string> {
        return this.getSettings(oemCode).pipe(
            map(
                (settings) =>
                    `Your permissions do not allow you to access this function. Contact your system administrator to update your permissions. For additional help, reach out to the EZ DOCS support team at ${settings.supportPhone} or ${settings.supportEmail}.`
            )
        );
    }

    public upgradedPackageMissingError(oemCode?: string): Observable<string> {
        return this.getSettings(oemCode).pipe(
            map((settings) => `This function is available as part of an upgraded EZ DOCS package. Contact us at ${settings.supportPhone} to learn more`)
        );
    }

    public async getAccessRestrictedContent(dealerId?: number, oemCode?: string, permissionIds?: UserPermissionIds[]): Promise<Observable<string>> {
        const userDealerEnrollment = await firstValueFrom(this.userService.getPermissions(dealerId, this.userService.user?.userId.toString()));
        let errorType: 'permissions' | 'enrollment';

        permissionIds.forEach((permissionId) => {
            if (userDealerEnrollment.find((permission) => permission.userPermissionId === permissionId && permission.active)) {
                errorType = 'permissions';
            } else {
                errorType = 'enrollment';
            }
        });

        switch (errorType) {
            case 'permissions':
                return this.permissionsMissingError(oemCode);
            case 'enrollment':
                return this.upgradedPackageMissingError(oemCode);
            default:
                return this.permissionsMissingError(oemCode);
        }
    }
}
